import Phaser from 'phaser';
import { callGameStart } from '../utils/gamebox';

export default class TitleScene extends Phaser.Scene {
  private logoImage?: Phaser.GameObjects.Image;
  private startButton?: Phaser.GameObjects.Rectangle;
  private startButtonText?: Phaser.GameObjects.Text;
  private howToPlayButton?: Phaser.GameObjects.Rectangle;
  private howToPlayText?: Phaser.GameObjects.Text;
  private modalBackground?: Phaser.GameObjects.Rectangle;
  private modalElements: Phaser.GameObjects.GameObject[] = [];
  private isModalOpen: boolean = false;
  private bgm?: Phaser.Sound.BaseSound;

  constructor() {
    super({ key: 'TitleScene' });
  }

  create(): void {
    // 背景色を設定
    this.cameras.main.setBackgroundColor(0x72aabf);
    
    // フォントが確実に読み込まれるまで待つ
    this.waitForFontsAndCreate();
  }

  private waitForFontsAndCreate(): void {
    // フォントが読み込まれているか確認
    const tiny5Loaded = document.fonts && document.fonts.check ? document.fonts.check('48px Tiny5') : false;
    const dotGothic16Loaded = document.fonts && document.fonts.check ? document.fonts.check('32px DotGothic16') : false;
    
    if (tiny5Loaded && dotGothic16Loaded) {
      // フォントが読み込まれている場合はすぐに作成
      this.createTexts();
    } else if (document.fonts && document.fonts.ready) {
      // フォントがまだ読み込まれていない場合は、readyイベントを待つ
      document.fonts.ready.then(() => {
        // 少し待ってから作成（フォントが確実に適用されるように）
        this.time.delayedCall(100, () => {
          this.createTexts();
        });
      });
    } else {
      // document.fonts APIが利用できない場合は、少し待ってから作成
      this.time.delayedCall(500, () => {
        this.createTexts();
      });
    }
  }

  private createTexts(): void {
    const { width, height } = this.scale;
    
    // 既存の要素があれば削除
    if (this.logoImage) {
      this.logoImage.destroy();
    }
    if (this.startButton) {
      this.startButton.destroy();
    }
    if (this.startButtonText) {
      this.startButtonText.destroy();
    }
    
    // フォントが確実に読み込まれているか再確認
    const tiny5Loaded = document.fonts && document.fonts.check ? document.fonts.check('48px Tiny5') : false;
    const dotGothic16Loaded = document.fonts && document.fonts.check ? document.fonts.check('32px DotGothic16') : false;
    
    // ロゴ画像を表示
    this.logoImage = this.add.image(width / 2, height / 2 - 100, 'logo');
    this.logoImage.setOrigin(0.5);
    this.logoImage.setDepth(100);
    // ロゴのサイズを適切に調整（必要に応じて）
    const logoScale = Math.min(width * 0.6 / this.logoImage.width, height * 0.2 / this.logoImage.height);
    this.logoImage.setScale(logoScale);

    // PLAYボタンを作成（HOMEやHow to playと同じスタイル）
    const buttonWidth = 300;
    const buttonHeight = 80;
    const buttonX = width / 2;
    const buttonY = height / 2 + 100;
    
    // ボタンの背景（黄色で視認性を向上）
    this.startButton = this.add.rectangle(buttonX, buttonY, buttonWidth, buttonHeight, 0xFFD66B, 1);
    this.startButton.setDepth(100);
    this.startButton.setInteractive({ useHandCursor: true });
    this.startButton.on('pointerdown', () => {
      // クリック時に色を変更
      if (this.startButton) {
        this.startButton.setFillStyle(0xD9B65B, 1);
      }
    });
    this.startButton.on('pointerup', () => {
      // 離した時に色を元に戻し、ボタンの動作を実行
      if (this.startButton) {
        this.startButton.setFillStyle(0xFFD66B, 1);
      }
      // モーダルが開いていない場合のみゲーム開始
      if (!this.isModalOpen) {
        // ボタン効果音を再生
        const buttonSound = this.sound.add('button', { volume: 0.5 });
        buttonSound.play();
        // GMOかんたんゲームボックスのgame_startを呼ぶ
        callGameStart(() => {
          // BGMを開始してからゲームシーンへ遷移
          this.startBGM();
          this.scene.start('PlayScene');
        });
      }
    });
    this.startButton.on('pointerout', () => {
      // ボタン外に出た時に色を元に戻す
      if (this.startButton) {
        this.startButton.setFillStyle(0xFFD66B, 1);
      }
    });
    this.startButton.on('pointercancel', () => {
      // タッチがキャンセルされた時（スワイプなど）に色を元に戻す
      if (this.startButton) {
        this.startButton.setFillStyle(0xFFD66B, 1);
      }
    });
    
    // ボタンテキスト
    this.startButtonText = this.add.text(buttonX, buttonY, 'PLAY', {
      fontSize: '48px',
      fontFamily: tiny5Loaded ? 'Tiny5' : 'Arial',
      color: '#000000',
      fontStyle: 'bold',
    });
    this.startButtonText.setOrigin(0.5);
    this.startButtonText.setDepth(101);
    // フォントが後から読み込まれた場合は更新
    if (!tiny5Loaded && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (this.startButtonText) {
          this.startButtonText.setFontFamily('Tiny5');
        }
      });
    }
    // テキストはインタラクティブにしない（ボタンが処理する）

    // How to playボタンを作成
    this.createHowToPlayButton();
  }

  private createHowToPlayButton(): void {
    const { width, height } = this.scale;
    
    // How to playボタン
    this.howToPlayButton = this.add.rectangle(width / 2, height / 2 + 200, 200, 60, 0xFFD66B, 1);
    this.howToPlayButton.setDepth(100);
    this.howToPlayButton.setInteractive({ useHandCursor: true });
    this.howToPlayButton.on('pointerdown', () => {
      // クリック時に色を変更
      if (this.howToPlayButton) {
        this.howToPlayButton.setFillStyle(0xD9B65B, 1);
      }
    });
    this.howToPlayButton.on('pointerup', () => {
      // 離した時に色を元に戻し、ボタンの動作を実行
      if (this.howToPlayButton) {
        this.howToPlayButton.setFillStyle(0xFFD66B, 1);
      }
      // ボタン効果音を再生
      const buttonSound = this.sound.add('button', { volume: 0.5 });
      buttonSound.play();
      this.openHowToPlayModal();
    });
    this.howToPlayButton.on('pointerout', () => {
      // ボタン外に出た時に色を元に戻す
      if (this.howToPlayButton) {
        this.howToPlayButton.setFillStyle(0xFFD66B, 1);
      }
    });
    this.howToPlayButton.on('pointercancel', () => {
      // タッチがキャンセルされた時（スワイプなど）に色を元に戻す
      if (this.howToPlayButton) {
        this.howToPlayButton.setFillStyle(0xFFD66B, 1);
      }
    });

    // フォントが確実に読み込まれているか確認
    const tiny5Loaded = document.fonts && document.fonts.check ? document.fonts.check('32px Tiny5') : false;
    
    this.howToPlayText = this.add.text(width / 2, height / 2 + 200, 'How to play', {
      fontSize: '32px',
      fontFamily: tiny5Loaded ? 'Tiny5' : 'Arial',
      color: '#000000',
    });
    this.howToPlayText.setOrigin(0.5);
    this.howToPlayText.setDepth(101);
    // フォントが後から読み込まれた場合は更新
    if (!tiny5Loaded && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (this.howToPlayText) {
          this.howToPlayText.setFontFamily('Tiny5');
        }
      });
    }
    // テキストはインタラクティブにしない（ボタンが処理する）
  }

  private openHowToPlayModal(): void {
    if (this.isModalOpen) return;
    
    this.isModalOpen = true;
    const { width, height } = this.scale;
    
    // モーダル本体
    const modalWidth = width * 0.85;
    // 内容に合わせて適切な高さを計算
    // タイトル(60px) + タイトル下余白(80px) + 説明文11行(11 * 45px) + 下部余白(100px) = 約735px
    // 画面高さに対して適切な比率を設定
    const modalHeight = Math.min(height * 0.85, 800); // 最大800px、または画面の85%（高さを広げる）
    const modalX = width / 2;
    const modalY = height / 2;
    
    const modalBg = this.add.rectangle(modalX, modalY, modalWidth, modalHeight, 0xffffff, 1);
    modalBg.setDepth(201);
    modalBg.setStrokeStyle(4, 0x000000);
    modalBg.setInteractive(); // モーダル本体もインタラクティブにして、イベントをキャッチ
    modalBg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // モーダル本体をクリックした場合は何もしない（閉じない）
      if (pointer.event) {
        pointer.event.stopPropagation();
      }
    });
    this.modalElements.push(modalBg);

    // モーダル背景（半透明の黒）- モーダル本体の後ろに配置
    this.modalBackground = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.7);
    this.modalBackground.setDepth(200);
    this.modalBackground.setInteractive();
    this.modalBackground.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // クリック位置を取得
      const clickX = pointer.x;
      const clickY = pointer.y;
      
      // モーダル本体の範囲を計算
      const modalLeft = modalX - modalWidth / 2;
      const modalRight = modalX + modalWidth / 2;
      const modalTop = modalY - modalHeight / 2;
      const modalBottom = modalY + modalHeight / 2;
      
      // モーダル本体の範囲外をクリックした場合のみ閉じる
      if (clickX < modalLeft || clickX > modalRight || clickY < modalTop || clickY > modalBottom) {
        this.closeHowToPlayModal();
      }
    });
    // modalBackgroundはmodalElementsに追加しない（閉じる判定のため）

    // タイトル
    const modalTitle = this.add.text(modalX, modalY - modalHeight / 2 + 60, 'How to Play', {
      fontSize: '56px',
      fontFamily: 'Tiny5',
      color: '#000000',
      fontStyle: 'bold',
    });
    modalTitle.setOrigin(0.5);
    modalTitle.setDepth(202);
    this.modalElements.push(modalTitle);

    // 遊び方のテキスト（日本語版、詳細版）
    const instructions = [
      '・画面をタップすると猫がジャンプします',
      '・猫は自動的に魚の方向へ向かって飛びます',
      '・魚に触れると1ポイント獲得できます',
      '・魚を獲得すると制限時間がリセットされます',
      '・最初のジャンプ後、地面が中央から',
      '  左右へ崩落していきます',
      '・犬に触れるとゲームオーバーになります',
      '・画面下部に落下してもゲームオーバーになります',
      '・タイマーバーが0になるとゲームオーバーになります',
      '・できるだけ多くのポイントを獲得することを',
      '  目指しましょう！'
    ];

    // テキストのサイズと間隔を調整
    const instructionFontSize = '20px'; // はみ出さないようにさらに小さく
    const instructionSpacing = 45; // 行間を調整（38 → 45に広げる）
    const startY = modalY - modalHeight / 2 + 160; // タイトルの下から開始（余白を広げる：140 → 160）
    const leftMargin = modalX - modalWidth / 2 + 40; // 左マージン40px
    const rightMargin = 80; // 右マージン80px（画面右端の余白を広げる）
    // テキスト幅 = モーダル幅 - 左マージン(40px) - 右マージン(80px)
    const textWidth = modalWidth - 40 - rightMargin; // 画面右端の余白を正しく確保

    instructions.forEach((instruction, index) => {
      const text = this.add.text(leftMargin, startY + (index * instructionSpacing), instruction, {
        fontSize: instructionFontSize,
        fontFamily: 'DotGothic16', // 日本語テキストにDotGothic16フォントを使用
        color: '#000000',
        wordWrap: { width: textWidth }, // モーダル幅から余白を引いた幅で折り返し
      });
      text.setOrigin(0, 0.5); // 左寄せ（X: 0, Y: 0.5）
      text.setDepth(202);
      this.modalElements.push(text);
    });

    // 閉じるボタン
    const closeButton = this.add.rectangle(modalX, modalY + modalHeight / 2 - 80, 150, 50, 0x666666, 1);
    closeButton.setDepth(202);
    closeButton.setInteractive({ useHandCursor: true });
    closeButton.on('pointerdown', () => {
      // クリック時に色を変更
      closeButton.setFillStyle(0x555555, 1);
    });
    closeButton.on('pointerup', () => {
      // 離した時に色を元に戻し、ボタンの動作を実行
      closeButton.setFillStyle(0x666666, 1);
      // ボタン効果音を再生
      const buttonSound = this.sound.add('button', { volume: 0.5 });
      buttonSound.play();
      this.closeHowToPlayModal();
    });
    closeButton.on('pointerout', () => {
      // ボタン外に出た時に色を元に戻す
      closeButton.setFillStyle(0x666666, 1);
    });
    closeButton.on('pointercancel', () => {
      // タッチがキャンセルされた時（スワイプなど）に色を元に戻す
      closeButton.setFillStyle(0x666666, 1);
    });
    this.modalElements.push(closeButton);

    const closeText = this.add.text(modalX, modalY + modalHeight / 2 - 80, 'Close', {
      fontSize: '32px',
      fontFamily: 'Tiny5',
      color: '#ffffff',
    });
    closeText.setOrigin(0.5);
    closeText.setDepth(203);
    // テキストはインタラクティブにしない（ボタンが処理する）
    this.modalElements.push(closeText);
  }

  private closeHowToPlayModal(): void {
    if (!this.isModalOpen) return;
    
    // モーダルを閉じる前に、フラグを設定してゲーム開始を防ぐ
    this.isModalOpen = false;
    
    // モーダルの全ての要素を削除
    this.modalElements.forEach(element => {
      if (element) {
        element.destroy();
      }
    });
    this.modalElements = [];
    
    // モーダル背景も削除
    if (this.modalBackground) {
      this.modalBackground.destroy();
      this.modalBackground = undefined;
    }
  }

  /**
   * BGMを開始
   */
  private startBGM(): void {
    // BGMが既に再生されている場合は何もしない
    if (this.bgm && this.bgm.isPlaying) {
      return;
    }
    
    // BGMを取得または作成
    if (!this.bgm) {
      this.bgm = this.sound.add('bgm', { loop: true, volume: 0.5 });
    }
    
    // BGMを再生
    if (!this.bgm.isPlaying) {
      this.bgm.play();
    }
  }
}

